/**
 * Fleets
 */

// React
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';

// Redux
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  getFleetsAction,
  updateFleetActions,
  getVehiclePreferredSourcesAction,
  updateVehiclePreferredSourcesAction,
  getFleetShiftsAction,
  updateFleetShiftsAction,
  updateFleetStatusActions,
} from './actions';
import { Store } from '../../redux/reducers';

// Selector

import { createStructuredSelector } from 'reselect';
import {
  makeSelectFleetsList,
  makeSelectLoading,
  makeSelectError,
  makeSelectCountList,
  makeSelectCentersList,
  makeSelectPreferredSources,
  makeSelectPreferredSourcesLoading,
  makeSelectFleetShifts,
  makeSelectFleetShiftsLoading,
} from './selectors';

// UI
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from '@material-ui/core';

// Components
import List from './list';
import Pagination from '@material-ui/lab/Pagination';

// Type
import { Props, SelectorProps, Payload, Fleet, FleetShift } from './interfaces';
import Icon from '@mdi/react';
import {
  mdiCarInfo,
  mdiCarKey,
  mdiCarMultiple,
  mdiClose,
  mdiPlus,
  mdiStateMachine,
  mdiTextBoxMultipleOutline,
  mdiCheck,
} from '@mdi/js';
import RenderFilter, { Filter } from '../../utils/listDaynamicFilterHookForm';
import {
  removeEmptyValueObject,
  translate,
} from '../../utils/helpers';
import Can from '../../components/Can';
import AuthContext from '../../authContext';
import { useForm } from 'react-hook-form';
import theme from '../../theme';
import Modal from '../../components/Modal';
import Detail from './detail';
import Add from './add';
import Edit from './edit';
import useQuery from '../../utils/hook/useQuery';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      padding: '10px 0',
      borderRadius: '4px',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },
    input: {
      display: 'flex',
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 8,
      marginRight: '10px',
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    textArea: {
      width: '100%',
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      marginTop: '30px',
      top: '47px',
      right: 0,
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    showCount: {
      '& .MuiSelect-icon': {
        color: '#DFA01F',
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: '#f30000',
      },
    },
    checkIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: '#039590',
      },
    },
  })
);

const Fleets: FunctionComponent<Props> = (props: Props) => {
  const authContext = useContext(AuthContext);
  const { register, handleSubmit, reset } = useForm<Inputs>();
  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 10,
  } as any, '');
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );
  const [filters, setFilters] = useState([] as Filter[]);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openDetailPage, setOpenDetailPage] = React.useState(false as boolean);
  const [openEditPage, setOpenEditPage] = React.useState(false as boolean);
  const [openCreatePage, setOpenCreatePage] = React.useState(false as boolean);
  const [openShiftDialog, setOpenShiftDialog] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [detailPageId, setDetailPageId] = useState(-1 as number);
  const [editPageId, setEditPageId] = useState(-1 as number);
  const [previewFleet, setPreviewFleet] = useState({} as Fleet);
  const [openFilterBox, setOpenFilterBox] = React.useState(false);
  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش دهنده',
      'customerName.equals': 'نام سفارش دهنده:',
      'customerName.contais': 'نام سفارش دهنده:',
      trackingCode: 'کد پیگیری',
      'trackingCode.equals': 'کد پیگیری',
      'trackingCode.contains': 'کد پیگیری',
      'id.equals': 'شناسه سفارش:',
      'id.contains': 'شناسه سفارش:',
      deliveryDate: 'زمان تحویل',
      'deliveryDate.equals': 'زمان تحویل:',
      'deliveryDate.contains': 'زمان تحویل:',
      status: 'وضعیت سفارش',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };
  const findFleet = (id: number) => {
    const status = props.fleetsList.find(fleet => fleet.id === id)?.status;
    return status && translate(status, 'center');
  };

  useEffect(() => {
    getFleets();
    setupFilters();
  }, [filtersValues]);
  // eslint-disable-line react-hooks/exhaustive-deps
  const classes = useStyles();

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };

  const handleClick = () => {
    setOpenFilterBox(prev => !prev);
  };

  const handleDetailPage = (id: number) => {
    setDetailPageId(id);
    setOpenDetailPage(true);
  };

  const handleEditPage = (id: number) => {
    setEditPageId(id);
    setOpenEditPage(true);
  };

  const handleCloseSelect = () => {
    setOpenSize(false);
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };

  const getFleets = () => {
    const { page, size } = filtersValues;
    props.fleetsDispatch({
      ...filtersValues,
      page,
      size,
    });
  };

  const updateFleet = () => {
    props.updateFleetDispatch({ body: previewFleet });
    setOpenConfirmUpdateState(false);
  };

  const updateFleetStatus = () => {
    props.updateFleetStatusDispatch({
      body: `"${previewFleet.status}"`,
      id,
    });
    setOpenConfirmUpdateState(false);
  };

  const setupFilters = () => {
    Can({
      role: authContext.user.role,
      perform: 'fleets:filtersByCenterId',
      yes: () => {
        setFilters([
          {
            type: 'input',
            id: 'name.contains',
            label: 'نام ناوگان',
            icon: mdiCarMultiple,
          },
          {
            type: 'number',
            id: 'id.equals',
            label: 'شناسه ناوگان',
            icon: mdiCarKey,
          },
          {
            type: 'checkbox',
            id: 'type.in',
            label: 'نوع ناوگان',
            data: ['THIRD_PARTY', 'PROPRIETARY'],
            translateType: 'FleetType',
            icon: mdiCarInfo,
          },
          {
            type: 'checkbox',
            id: 'status.in',
            label: 'وضعیت ناوگان',
            data: ['OFF', 'ACTIVE'],
            translateType: 'FleetStatus',
            icon: mdiStateMachine,
          },
        ]);
      },
      no: () => {
        setFilters([
          {
            type: 'input',
            id: 'name.contains',
            label: 'نام ناوگان',
            icon: mdiCarMultiple,
          },
          {
            type: 'number',
            id: 'id.equals',
            label: 'شناسه ناوگان',
            icon: mdiCarKey,
          },
          {
            type: 'checkbox',
            id: 'type.in',
            label: 'نوع ناوگان',
            data: ['THIRD_PARTY', 'PROPRIETARY'],
            translateType: 'FleetType',
            icon: mdiCarInfo,
          },
          {
            type: 'select',
            id: 'status.equals',
            label: 'وضعیت ناوگان',
            data: ['SUSPENDED', 'ACTIVE'],
            translateType: 'FleetStatus',
            icon: mdiStateMachine,
          },
        ]);
      },
    });
  };
  let headCells = [];

  Can({
    role: authContext.user.role,
    perform: 'orders:list:centerName',
    yes: () => {
      headCells = [
        { id: 'name', label: 'نام ناوگان', type: 'input' },
        {
          id: 'id',
          label: 'شناسه ناوگان',
          type: 'input',
        },
        { id: 'type', label: 'نوع ناوگان', type: 'input' },
        {
          id: 'driverCount',
          label: 'تعداد راننده',
          type: 'input',
        },
      ];
    },
    no: () => {
      headCells = [
        { id: 'name', label: 'نام ناوگان', type: 'input' },
        {
          id: 'id',
          label: 'شناسه ناوگان',
          type: 'input',
        },
        { id: 'type', label: 'نوع ناوگان', type: 'input' },
        {
          id: 'driverCount',
          label: 'تعداد راننده',
          type: 'input',
        },
      ];
    },
  });
  const head = { headCells };
  const onSubmit = (filtervalues: any) => {
    setFiltersValues({
      ...removeEmptyValueObject(filtervalues),
      page: 0,
      size: 10,
    });
    reset({});
    setOpenFilterBox(false);
  };

  const handleClickAway = (e: any) => {
    // TODO: Shet
    if (
      e.target.getAttribute('dir') !== 'rtl' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('class') !== 'PrivateSwitchBase-input-73' &&
      e.target.getAttribute('class') !==
      'MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock' &&
      e.target.getAttribute('aria-hidden') !== 'true'
    )
      setOpenFilterBox(false);
  };

  const handleOpenConfirmUpdate = (body: Fleet) => {
    const { id } = body;
    setOpenConfirmUpdateState(true);
    id && setId(id);
    setPreviewFleet(body);
  };

  const handleOpenShiftDialog = (body: Fleet) => {
    const { id, shiftId } = body;
    setOpenShiftDialog(true);
    setId(id);
    setPreviewFleet(body);
    props.getFleetShiftsDispatch(shiftId);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={5}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h4" component="h2">
            {`لیست ناوگان ها`}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <RenderFilter
            filters={filters}
            onFilter={(filtervalues: any) => {
              setFiltersValues({
                ...removeEmptyValueObject(filtervalues),
                page: 0,
                size: 10,
              });
            }}
            mainFiltersCount={0}
            baseFilter={{
              type: 'input',
              id: 'name.contains',
              placeholder: 'نام ناوگان',
            }}
            filterValues={filtersValues}
          />
        </Grid>
        <Grid
          style={{ margin: 0 }}
          container
          spacing={5}
          alignItems="center"
          justify="space-between"
        >
          <Grid item>
            <div>
              <span style={{ color: '#8a8a8a' }}>{`تعداد کل ناوگان ها:`}</span>
              <span style={{ paddingRight: '10px' }}>{props.count}</span>
            </div>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Icon path={mdiPlus} size={1} />}
              onClick={() => setOpenCreatePage(true)}
            >
              {'ایجاد ناوگان جدید'}
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            {props.loading ? (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            ) : (
              <>
                <List
                  data={props.fleetsList}
                  sourcesList={props.centersList}
                  preferredSourcesLoading={props.preferredSourcesLoading}
                  preferredSources={props.preferredSources}
                  handleConfirmUpdate={handleOpenConfirmUpdate}
                  handleEditPage={handleEditPage}
                  handleOpenDetailPage={id => handleDetailPage(id)}
                  updateVehicleSource={
                    props.updateVehiclePreferredSourcesDispatch
                  }
                  head={head}
                  filterVal={filtersValues}
                  onFilterValue={(filtervalues: any) => {
                    setFiltersValues({
                      ...removeEmptyValueObject(filtervalues),
                      ...filtersValues,
                      page: 0,
                      size: 10,
                    });
                  }}
                  removeFilterHandler={removeFilterHandler}
                  filterLabelTranslator={filterTranslator}
                />
              </>
            )}
          </Paper>
        </Grid>
        <Grid container spacing={5} style={{ margin: 0 }}>
          <Grid id="showFilter" item xs={12} sm={6}>
            {props.fleetsList && (
              <FormControl
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                  {' '}
                  تعداد نمایش در هر صفحه:
                </div>
                <Select
                  open={openSize}
                  onClose={handleCloseSelect}
                  className={classes.showCount}
                  onOpen={() => setOpenSize(true)}
                  value={filtersValues.size}
                  onChange={handleChangeSizePage}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid xs={12} sm={6} container item direction="row-reverse">
            {props.fleetsList && (
              <Pagination
                count={Math.ceil(props.count / filtersValues.size)}
                color="secondary"
                page={filtersValues.page + 1}
                onChange={handleChangePage}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      <Modal
        maxWidth="lg"
        onClose={() => setOpenDetailPage(false)}
        open={openDetailPage}
        title={
          <div style={{ textAlign: 'center' }}>
            <Icon
              path={mdiTextBoxMultipleOutline}
              size={4}
              color={theme.palette.green.main}
            />
          </div>
        }
        content={<Detail id={detailPageId} />}
      />
      <Dialog
        fullWidth
        open={openConfirmUpdateState}
        onClose={() => setOpenConfirmUpdateState(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogContent>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            <IconButton
              aria-label="close"
              // className={classes.closeButton}
              onClick={() => setOpenConfirmUpdateState(false)}
            >
              <Icon path={mdiClose} size={1} />
            </IconButton>
          </div>
          <div
            className={
              previewFleet.status === 'ACTIVE'
                ? classes.checkIcon
                : classes.editIcon
            }
          >
            <Icon
              path={previewFleet.status === 'ACTIVE' ? mdiCheck : mdiClose}
              size={4}
              color={
                previewFleet.status === 'ACTIVE'
                  ? theme.palette.green.main
                  : '#f30000'
              }
            />
          </div>
          <div className={classes.cancelTitle}>
            {`از 
            "${translate(previewFleet.status, 'center')}"
            کردن ناوگان با شناسه 
            "${previewFleet.id}"
        مطمئن هستید؟`}
          </div>
        </DialogContent>
        <DialogActions className={classes.actionsBox}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={() => setOpenConfirmUpdateState(false)}
                variant="contained"
                color="secondary"
                size="large"
              >
                {'خیر'}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={updateFleetStatus}
                size="large"
              >
                {'بله'}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <Modal
        maxWidth="md"
        onClose={() => setOpenCreatePage(false)}
        open={openCreatePage}
        title={
          <div className={classes.checkIcon} style={{ textAlign: 'center' }}>
            <Icon
              path={mdiCarMultiple}
              size={4}
              color={theme.palette.green.main}
            />
          </div>
        }
        content={<Add onClose={() => setOpenCreatePage(false)} />}
      />
      <Modal
        maxWidth="md"
        onClose={() => setOpenEditPage(false)}
        open={openEditPage}
        title={
          <div className={classes.checkIcon} style={{ textAlign: 'center' }}>
            <Icon
              path={mdiTextBoxMultipleOutline}
              size={4}
              color={theme.palette.blue.main}
            />
          </div>
        }
        content={
          <Edit id={editPageId} onClose={() => setOpenEditPage(false)} />
        }
      />
    </div>
  );
};

const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
  fleetsList: makeSelectFleetsList(),
  centersList: makeSelectCentersList(),
  preferredSources: makeSelectPreferredSources(),
  count: makeSelectCountList(),
  preferredSourcesLoading: makeSelectPreferredSourcesLoading(),
  loading: makeSelectLoading(),
  fleetShifts: makeSelectFleetShifts(),
  fleetShiftsLoading: makeSelectFleetShiftsLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    fleetsDispatch: (payload: Payload) => dispatch(getFleetsAction(payload)),
    getVehiclePreferredSourcesDispatch: (id: number) =>
      dispatch(getVehiclePreferredSourcesAction(id)),
    getFleetShiftsDispatch: (id: number) =>
      dispatch(getFleetShiftsAction({ id })),
    updateVehiclePreferredSourcesDispatch: (payload: {
      id: number;
      body: number[];
    }) => dispatch(updateVehiclePreferredSourcesAction(payload)),
    updateFleetShiftsDispatch: (payload: { body: FleetShift }) =>
      dispatch(updateFleetShiftsAction(payload)),
    updateFleetDispatch: (payload: { body: Fleet }) =>
      dispatch(updateFleetActions(payload)),
    updateFleetStatusDispatch: (payload: Payload) =>
      dispatch(updateFleetStatusActions(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(Fleets);
